import {
  ReceivingReportRecord,
  RRMovement,
} from '../../../../types/ReceivingReport';
import { forwardRef } from 'react';
import { Space, Table, TableColumnType } from 'antd';
import { roundAndFormat } from '../../../../lib/utils';
import CustomDescriptionItem from '../../../../components/CustomDescriptionItem';

interface ReceivingReportPrintTemplateProps {
  data?: ReceivingReportRecord;
}

const ReceivingReportPrintTemplate = forwardRef<
  HTMLDivElement,
  ReceivingReportPrintTemplateProps
>(({ data }, ref) => {
  const columns: TableColumnType<RRMovement>[] = [
    {
      title: 'PRODER',
      dataIndex: 'proderNo',
      render: (_text, record) => (
        <span>{record?.proderNo}</span>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'productName',
      render: (_text, record) => <span>{record.product?.description}</span>,
    },
    {
      title: 'Request Quantity',
      dataIndex: 'requestQty',
      render: (_text, record) => (
        <span>{roundAndFormat(record.requestQty)}</span>
      ),
    },
    {
      title: 'Request UoM',
      dataIndex: 'requestUoM',
      render: (_text, record) => <span>{record.requestUom?.description}</span>,
    },
    {
      title: 'Product UoM',
      dataIndex: 'productUoM',
      render: (_text, record) => <span>{record.productUom?.description}</span>,
    },

    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      render: (_text, record) => (
        <span>{roundAndFormat(record.unitPrice)}</span>
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      render: (_text, record) => (
        <span>{roundAndFormat(record.unitPrice * record.requestQty)}</span>
      ),
    },
  ];

  const subTotal = data?.movements.reduce(
    (total, movement) => total + movement.unitPrice * movement.requestQty,
    0,
  );

  const grandTotal = ((subTotal || 0) + (data?.vat || 0)) - ((data?.wtax || 0) + (data?.discount || 0));
  
  return (
    <div ref={ref}>
      <div className={'p-4 items-center justify-center'}>
        <div className={'flex items-center justify-between pb-3'}>
          <div className={'flex'}>
            <img src={'/mmd/mmd.png'} alt={'mmd.png'} className={'w-10 h-10'} />
            <span className={'font-semibold text-2xl'}>
              Asia Ceramic Corporation
            </span>
          </div>
          <span className={'font-semibold text-2xl'}>Receiving Request</span>
        </div>
        <div>
          <div className="flex flex-row justify-between mb-2">
            <CustomDescriptionItem label={'RR'} content={data?.code} />
            <CustomDescriptionItem
              label={'Date'}
              content={data?.requiredDate}
            />
          </div>
          <div className="flex flex-row justify-between mb-2">
            <CustomDescriptionItem
              label={'Vendor Name'}
              content={data?.vendor?.description}
            />
            <CustomDescriptionItem
              label={'Destination'}
              content={data?.destination?.description}
            />
          </div>
          <div className="flex flex-row justify-between mb-2">
            <CustomDescriptionItem label={'Address of Vendor'} content={''} />
            <CustomDescriptionItem
              label={'P.O Number'}
              content={data?.source}
            />
          </div>
          <div className="flex flex-row justify-between mb-2">
            <CustomDescriptionItem
              label={'Purpose'}
              content={data?.rrPurpose}
            />
            <CustomDescriptionItem label={'D.R Number'} content={data?.drNo} />
            <CustomDescriptionItem label={'Invoice Number'} content={data?.invoiceNo} />
          </div>
        </div>
        <div>
          <Table
            rowKey={(record) => record.id}
            dataSource={data?.movements}
            columns={columns}
            pagination={false}
          />
        </div>

        <div className="flex justify-end mt-4">
          <Space direction="vertical">
            <div className="flex justify-between">
              <span>Sub Total:</span>
              <span>
                {roundAndFormat(subTotal)}
              </span>
            </div>
            <div className="flex flex-row justify-between">
              <span>Withholding Tax:</span>
              <span className={'ml-4'}>{roundAndFormat(data?.wtax ?? 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>VAT:</span>
              <span className={'ml-4'}>{roundAndFormat(data?.vat ?? 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>Discount:</span>
              <span className={'ml-4'}>
                {roundAndFormat(data?.discount ?? 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold">Grand Total:</span>
              <span className="font-semibold">
                {roundAndFormat(grandTotal)}
              </span>
            </div>
          </Space>
        </div>

        <div className="flex flex-row items-center justify-between pt-6">
          <div className="basis-1/4">
            <div className="flex flex-col">
              <div className="flex-1 w-full">
                <label>Approved By:</label>
              </div>
              <div className="flex-1 w-full border-b border-solid border-black">
                { data?.approvedBy || (<span>&nbsp;</span>) }
              </div>
              <div className="flex-1 w-full">
                <i>Purchasing Head</i>
              </div>
            </div>
          </div>
          <div className="basis-1/4">
            <div className="flex flex-col">
              <div className="flex-1 w-full">
                <label>Received By</label>
              </div>
              <div className="flex-1 w-full border-b border-solid border-black">
                { data?.createdBy || (<span>&nbsp;</span>) }
              </div>
              <div className="flex-1 w-full">
                <i>Signature</i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
ReceivingReportPrintTemplate.displayName = 'ReceivingReportPrintTemplate';
export default ReceivingReportPrintTemplate;
